import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Chip,
  Grid,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import ReactMarkdown from 'react-markdown';
import { StageProcessor } from '../components/StageProcessor';
import { generateMinimalRequirements, saveAnswers } from '../api/stageFunctions';
import { Question, QA, MFR } from '../types/pipeline';
import { useStageCache } from '../hooks/useStageCache';
import { STAGE_NUMBERS } from '../utils/cacheUtils';
import { getButtonText } from '../utils/cacheUIHelpers';

interface GenerateMFRProps {
  commodityDescription: string;
  questions: Question[];
  onComplete: (mfr: MFR) => void;
  flowId?: string | null;
  initialAnswers?: Record<string, string>;
}

const GenerateMFR: React.FC<GenerateMFRProps> = ({
  commodityDescription,
  questions,
  onComplete,
  flowId,
  initialAnswers = {},
}) => {
  console.log('[GenerateMFR] props.commodityDescription:', commodityDescription);
  const [answers, setAnswers] = useState<Record<string, string>>(initialAnswers);
  const [mfr, setMfr] = useState<MFR | null>(null);
  const [triggerGenerateMFR, setTriggerGenerateMFR] = useState(0);
  const [shouldProcess, setShouldProcess] = useState(false);
  const [useCachedData, setUseCachedData] = useState(true);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedMfrText, setEditedMfrText] = useState<string>('');
  
  // Debouncing for auto-save functionality
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  // Prepare Q&A data function (used by cache hook)
  const prepareQAData = useCallback((): QA[] => {
    return questions
      .filter(q => answers[q.id])
      .map(q => ({
        questionId: q.id,
        question: q.question,
        answer: answers[q.id],
        category: q.category,
      }));
  }, [questions, answers]);
  
  // Check cache for this stage (MFR generation)
  const { cachedData, isCacheValid, isCheckingCache, refreshCache } = useStageCache({
    flowId,
    stageNumber: STAGE_NUMBERS.MFR,
    inputData: {
      commodity_description: commodityDescription,
      questions_and_answers: prepareQAData(),
    },
    enabled: useCachedData && flowId !== null,
  });
  
  // Transform cached API response to MFR format
  const transformCachedMFR = useCallback((cachedData: any): MFR => {
    const requirements: any = {
      functional: [],
      performance: [],
      hardware: [],
      weapons: []
    };
    
    // Extract numbered requirements from the cached MFR text
    if (cachedData.minimum_requirements) {
      const lines = cachedData.minimum_requirements.split('\n');
      let currentCategory = 'functional';
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        // Check for section headers
        if (trimmedLine.includes('System Hardware')) {
          currentCategory = 'hardware';
        } else if (trimmedLine.includes('Simulated Weapons')) {
          currentCategory = 'weapons';
        } else if (trimmedLine.includes('Core Training') || trimmedLine.includes('General System')) {
          currentCategory = 'functional';
        }
        
        // Extract numbered requirements (e.g., 1.1, 2.3)
        if (/^\d+\.\d+/.test(trimmedLine)) {
          requirements[currentCategory].push(trimmedLine);
        }
      }
    }
    
    return {
      id: `mfr-cached-${Date.now()}`,
      commodityDescription: cachedData.original_spec || commodityDescription,
      generatedAt: new Date().toISOString(),
      requirements,
      questionsAnswered: prepareQAData(),
      rawText: cachedData.minimum_requirements || '',
      originalCommodityDescription: commodityDescription,
    };
  }, [commodityDescription, prepareQAData]);

  // Debounced save function
  const debouncedSave = useCallback(
    async (updatedAnswers: Record<string, string>) => {
      if (!flowId || questions.length === 0) {
        return;
      }
      
      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Set a new timeout for saving
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          setIsSaving(true);
          console.log('Auto-saving answers...', { flowId, answers: updatedAnswers });
          
          const response = await saveAnswers(flowId, questions, updatedAnswers);
          
          if (response.success) {
            setLastSaveTime(new Date());
            console.log('✅ Answers auto-saved successfully');
          } else {
            console.error('❌ Failed to auto-save answers:', response.error);
          }
        } catch (error) {
          console.error('❌ Error during auto-save:', error);
        } finally {
          setIsSaving(false);
        }
      }, 1000); // 1 second delay
    },
    [flowId, questions]
  );
  
  const handleAnswerChange = (questionId: string, value: string) => {
    console.log('handleAnswerChange called:', { questionId, value });
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionId]: value
      };
      console.log('Updated answers:', newAnswers);
      
      // Trigger debounced save
      debouncedSave(newAnswers);
      
      return newAnswers;
    });
  };

  const handleGenerateMFR = async () => {
    console.log('[GenerateMFR] handleGenerateMFR clicked');
    console.log('[GenerateMFR] current commodityDescription:', commodityDescription);
    console.log('[GenerateMFR] answers count:', Object.keys(answers).length);
    // Check if high priority questions are answered
    const importantQuestions = questions.filter(q => 
      q.priority === 'High' || q.priority === 1 || q.priority === 2
    );
    const answeredImportant = importantQuestions.every(q => answers[q.id]?.trim());
    
    if (!answeredImportant) {
      alert('Please answer all high priority questions before generating the MFR');
      return;
    }

    setShouldProcess(true);
    setTriggerGenerateMFR(prev => {
      const next = prev + 1;
      console.log('[GenerateMFR] triggerGenerateMFR ->', next, 'shouldProcess -> true');
      return next;
    });
  };

  const handleMFRGenerated = (mfrData: MFR) => {
    const enrichedMFR = {
      ...mfrData,
      commodityDescription: mfrData.commodityDescription || commodityDescription,
      originalCommodityDescription: commodityDescription,
    };
    setMfr(enrichedMFR);
    setEditedMfrText(enrichedMFR.rawText || '');
    // Don't call onComplete here - let user review MFR first
  };

  const handleModeChange = (event: React.MouseEvent<HTMLElement>, newMode: string | null) => {
    if (newMode !== null) {
      setIsEditMode(newMode === 'edit');
      // When switching back to preview mode, update the MFR with edited text
      if (newMode === 'preview' && mfr) {
        setMfr({
          ...mfr,
          rawText: editedMfrText
        });
      }
    }
  };


  // Calculate statistics
  const importantQuestions = questions.filter(q => 
    q.priority === 'High' || q.priority === 1 || q.priority === 2
  );
  const answeredCount = Object.values(answers).filter(a => a.trim()).length;
  const importantAnsweredCount = importantQuestions.filter(q => answers[q.id]?.trim()).length;
  const canGenerateMFR = importantQuestions.every(q => answers[q.id]?.trim());

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          <QuestionAnswerIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Stage 2: Answer Questions & Define Salient Characteristics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Answer the clarifying questions to define the Salient Characteristics for your requirement.
          High priority questions must be answered before generating the Salient Characteristics document.
        </Typography>
        
        {/* Progress indicators */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={`${answeredCount} of ${questions.length} answered`}
            color={answeredCount === questions.length ? 'success' : 'default'}
          />
          <Chip
            label={`${importantAnsweredCount} of ${importantQuestions.length} high priority`}
            color={importantAnsweredCount === importantQuestions.length ? 'success' : 'warning'}
          />
          {flowId && (
            <Chip
              label={
                isSaving 
                  ? 'Saving...' 
                  : lastSaveTime 
                  ? `Last saved: ${lastSaveTime.toLocaleTimeString()}`
                  : 'Auto-save enabled'
              }
              color={isSaving ? 'default' : 'info'}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Paper>


      {/* Mock mode notification */}
      {parseInt(process.env.REACT_APP_MOCK_UP_TO_STAGE || '0') >= 2 && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          Mock mode active - Questions may have sample answers for testing
        </Alert>
      )}

      {/* Questions and Answers - Only show if MFR not yet generated */}
      {!mfr && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Answer Clarifying Questions
          </Typography>
          
          <Grid container spacing={3}>
            {questions.map((q) => (
              <Grid item xs={12} key={q.id}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label={q.category} 
                      size="small" 
                      color="primary" 
                      sx={{ mr: 1 }} 
                    />
                    <Chip 
                      label={q.priority} 
                      size="small" 
                      color={q.priority === 'High' ? 'error' : q.priority === 'Medium' ? 'warning' : 'success'}
                      sx={{ mr: 1 }}
                    />
                    {(q.priority === 'High' || q.priority === 1 || q.priority === 2) && (
                      <Typography variant="caption" color="error">
                        * Required
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {q.question}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    value={answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    placeholder={q.helpText || 'Enter your answer...'}
                    required={q.priority === 'High' || q.priority === 1 || q.priority === 2}
                    error={
                      (q.priority === 'High' || q.priority === 1 || q.priority === 2) && 
                      !answers[q.id]?.trim()
                    }
                  />
                  {q.rationale && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Rationale: {q.rationale}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Salient Characteristics Generation Controls - Only show if not yet generated */}
      {!mfr && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            <DescriptionIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
            Generate Salient Characteristics
          </Typography>
          
          <FormControlLabel
          control={
            <Checkbox
              checked={useCachedData}
              onChange={(e) => setUseCachedData(e.target.checked)}
              name="useCachedData"
              disabled={isCheckingCache || !isCacheValid}
            />
          }
          label="Use cached data when available"
        />
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {canGenerateMFR 
              ? 'All required questions answered. Ready to generate Salient Characteristics.' 
              : `Please answer ${importantQuestions.length - importantAnsweredCount} more high priority questions.`
            }
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<DescriptionIcon />}
            onClick={handleGenerateMFR}
            disabled={!canGenerateMFR || isCheckingCache}
          >
            {isCheckingCache ? 'Checking cache...' : getButtonText('Generate Salient Characteristics', useCachedData, cachedData && cachedData.mfr)}
          </Button>
        </Box>
      </Paper>
      )}

      {/* Salient Characteristics Generation Processor - always mounted, triggered by state */}
      <StageProcessor
        title="Generating Salient Characteristics"
        description="Creating Salient Characteristics based on your answers..."
        processFunction={() => {
          console.log('[GenerateMFR] StageProcessor.processFunction called');
          console.log('[GenerateMFR] Passing commodityDescription to API:', commodityDescription);
          return generateMinimalRequirements(prepareQAData(), commodityDescription, flowId);
        }}
        onSuccess={handleMFRGenerated}
        trigger={shouldProcess ? triggerGenerateMFR : 0}
        autoProcess={true}
        showStatus={true}
      >
        {(data, status) => {
          if (status.isProcessing) {
            return (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {status.message}
                </Typography>
              </Box>
            );
          }
          return null;
        }}
      </StageProcessor>

      {/* Generated Salient Characteristics Display */}
      {mfr && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Generated Salient Characteristics
          </Typography>
          
          <Alert severity="success" sx={{ mb: 2 }}>
            Salient Characteristics generated successfully! Review the requirements below.
          </Alert>

          {/* Mode Toggle */}
          <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={isEditMode ? 'edit' : 'preview'}
              exclusive
              onChange={handleModeChange}
              aria-label="document mode"
            >
              <ToggleButton value="preview" aria-label="preview mode">
                <PreviewIcon sx={{ mr: 1 }} />
                Preview
              </ToggleButton>
              <ToggleButton value="edit" aria-label="edit mode">
                <EditIcon sx={{ mr: 1 }} />
                Edit
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Commodity: {mfr.commodityDescription?.substring(0, 100)}
              {mfr.commodityDescription && mfr.commodityDescription.length > 100 && '...'}
            </Typography>
            
            {/* Display MFR - either editable or preview */}
            {isEditMode ? (
              <TextField
                fullWidth
                multiline
                value={editedMfrText}
                onChange={(e) => setEditedMfrText(e.target.value)}
                variant="outlined"
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                    fontSize: '14px'
                  }
                }}
                minRows={20}
                maxRows={30}
              />
            ) : (
              <Paper 
                sx={{ 
                  p: 3, 
                  mt: 2,
                  maxHeight: '500px', 
                  overflowY: 'auto',
                  backgroundColor: '#f5f5f5',
                  '& h1, & h2, & h3': { 
                    color: '#333',
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '0.3em',
                    marginTop: '1em'
                  },
                  '& ul': { paddingLeft: '2em' },
                  '& li': { marginBottom: '0.5em' },
                  '& p': { marginBottom: '1em' },
                  '& strong': { color: '#1976d2' }
                }}
              >
                <ReactMarkdown>
                  {editedMfrText || mfr.rawText || 'No requirements text available'}
                </ReactMarkdown>
              </Paper>
            )}
          </Box>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => {
                // Update MFR with edited text before continuing
                const finalMfr = {
                  ...mfr,
                  rawText: editedMfrText
                };
                onComplete(finalMfr);
              }}
            >
              Continue to Product Discovery
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default GenerateMFR;
